import {
    Dialog,
    Stack,
    Button,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    DialogProps,
    IconButton,
    Backdrop,
    CircularProgress,
    Typography,
} from '@mui/material';
import type { Phrase } from 'types/types';
import CloseIcon from '@mui/icons-material/Close';

interface ConfirmDialogProps extends Omit<DialogProps, 'title'> {
    title?: string | React.ReactNode;
    open: boolean;
    children?: React.ReactNode;
    cancelText?: Phrase | undefined;
    actionText?: Phrase | undefined;
    hideCloseButton?: boolean;
    handleClose: () => void;
    actionHandler?: () => void | Promise<void>;
    actionHandlerSubmitting?: boolean;
    isLoading?: boolean;
    fullwidthButtons?: boolean;
}

export const ConfirmDialog = ({
    title,
    open,
    children,
    cancelText,
    actionText,
    hideCloseButton,
    handleClose,
    actionHandler,
    actionHandlerSubmitting,
    isLoading = false,
    fullwidthButtons,
    ...props
}: ConfirmDialogProps) => (
    <>
        <Dialog
            {...props}
            open={open}
            onClose={handleClose}
            aria-labelledby="confirm-dialog-title"
            aria-describedby="confirm-dialog-description"
        >
            {title && (
                <DialogTitle id="confirm-dialog-title">
                    <Stack
                        direction="row"
                        spacing={2}
                        sx={{
                            alignItems: 'center',
                            justifyContent: 'space-between',
                        }}
                    >
                        {typeof title === 'string' && <Typography variant="h5">{title}</Typography>}
                        {typeof title !== 'string' && title}
                        {!hideCloseButton && (
                            <IconButton
                                aria-label="close"
                                onClick={handleClose}
                                sx={{
                                    right: -8,
                                    color: (theme) => theme.palette.grey[500],
                                }}
                            >
                                <CloseIcon />
                            </IconButton>
                        )}
                    </Stack>
                </DialogTitle>
            )}
            {children && (
                <DialogContent sx={{ p: 3 }}>
                    <DialogContentText component="div" id="confirm-dialog-description">
                        {children}
                    </DialogContentText>
                </DialogContent>
            )}
            {(cancelText || (actionText && actionHandler)) && (
                <DialogActions sx={{ p: 2 }}>
                    <Stack
                        direction={fullwidthButtons ? 'column' : 'row'}
                        spacing={2}
                        sx={{
                            display: 'flex',
                            width: '100%',
                            justifyContent: fullwidthButtons ? 'center' : 'flex-end',
                        }}
                    >
                        {cancelText && (
                            <Button
                                sx={{
                                    minWidth: 100,
                                    width: fullwidthButtons ? '100%' : 'auto',
                                }}
                                variant="outlined"
                                onClick={handleClose}
                            >
                                {cancelText}
                            </Button>
                        )}
                        {actionText && actionHandler && (
                            <Button
                                sx={{
                                    minWidth: 100,
                                    width: fullwidthButtons ? '100%' : 'auto',
                                }}
                                loading={actionHandlerSubmitting}
                                disabled={actionHandlerSubmitting}
                                variant="contained"
                                onClick={actionHandler}
                                autoFocus
                            >
                                {actionText}
                            </Button>
                        )}
                    </Stack>
                </DialogActions>
            )}
        </Dialog>
        {isLoading && (
            <Backdrop
                open
                sx={{
                    zIndex: (theme) => theme.zIndex.modal + 1,
                }}
            >
                <CircularProgress color="primary" data-testid="progressindicator" />
            </Backdrop>
        )}
    </>
);
